/**
 * 이상한 방 - 루프백 전송 (호스트를 안은 전송 · 봇과 한 판)
 *
 * 근거 문서: `이상한방-상태모델-v0.1.md` 9절(CPU 상대) · 11.2절(클라이언트가 가진 것)
 *            로드맵 M2(`loopbackTransport` - 모든 메시지가 `encode → decode`를 통과한다 · 배달은 `queueMicrotask`)
 *            브리프 "서버 없이도 CPU 상대 멀티가 돌아가야 한다 - 첫 마일스톤의 검증 수단"
 *
 * 같은 프로세스 안에 호스트(`matchHost.ts`)를 안고, 세션에게는 소켓 너머의 서버와 **같은 표면**(`Transport`)을 준다.
 * 세션은 자기가 루프백 위에 있는지 모른다 - 그래야 봇 판에서 검증한 세션 코드가 서버 판에서 그대로 돈다.
 *
 * ## 모든 메시지가 `encode → decode`를 통과한다
 *
 * 같은 프로세스라 객체를 그대로 건네도 "돌아간다". 그렇게 하지 않는 이유가 둘이고 장치는 하나다:
 *
 *  1. **참조 공유 차단.** 호스트의 `state.original`과 클라이언트가 복원한 방이 같은 `Map`을 들면 "두 스냅샷이
 *     독립이다"(상태모델 2절)가 타입으로만 참이 된다. 클라이언트가 실수로 방을 건드리면 호스트의 정답표가 흔들린다.
 *  2. **직렬화 드리프트 조기 발견.** 뷰나 효과에 `Map`·`Set`이 하나 끼면 JSON이 조용히 `{}`로 바꾼다. 루프백이 JSON을
 *     건너뛰면 그 사고는 서버 판(M6)에서 처음 드러난다 - 가장 재현하기 어려운 자리다. 여기서 매 메시지가 JSON을 지나면
 *     봇 판의 첫 프레임에서 터진다.
 *
 * `structuredClone`은 1만 해결하고 2를 못 잡는다(`Map`을 살려 둔다). 그래서 `protocol.encode/decode`다.
 * 디코드 실패는 **던진다** - 호스트가 만든 메시지가 자기 코덱을 못 지나는 것은 프로그래밍 오류이고, 조용히 버리면
 * "가끔 스텝이 안 온다"로 변장한다.
 *
 * ## 배달은 `queueMicrotask`
 *
 * `send()`의 콜스택 안에서 `onMessage` 구독자가 불리면 세션은 "보냈더니 판정이 즉시 왔다"를 보게 되고, 그 순서에 기대는
 * 코드가 생긴다 - 서버 판에서는 그 순서가 100ms 뒤다. 마이크로태스크 하나만 끼워도 "같은 콜스택"이라는 가정은 성립하지
 * 않고, 프레임은 넘기지 않아 봇 판의 체감은 그대로다. 낙관적 표식이 회색 하나뿐인 이유(로드맵 2.5절)와 같은 결정이다.
 *
 * ## 프레임 델타가 시계다 - 서버와 다른 단 하나
 *
 * 호스트는 `setInterval`을 모르고 바깥이 `tick(atMs)`를 불러야 한다. 루프백의 바깥은 프레임 루프다: 세션이 프레임마다
 * `transport.tick(dt)`를 부르고 이 파일이 그것을 누적해 호스트 시각으로 넘긴다. 그래서 **설정 패널이 열려 프레임 루프가
 * 멈추면 루프백의 판도 멈춘다** - 봇도 마감도 같이 선다. 서버(M6)는 멈추지 않는다(서버 시계는 사람의 탭을 모른다).
 * 봇 판에서는 이것이 옳다 - 혼자 하는 판에서 화면을 덮은 채 시간이 흐르면 그것이 곧 손해다. 사람 판에서는 옳지 않고,
 * 그래서 소켓 전송의 `tick`은 아무것도 하지 않는다(`transport.ts`).
 *
 * `net/`은 `core`·`game/match`·`game` 리프만 본다(`boundary.test.ts`).
 */

import type { PlayerId } from '../core/types'
import { botProfileFor, type BotDifficulty } from '../game/match/cpu'
import type { MatchConfig, MatchDeps } from '../game/match/matchTypes'
import { createMatchHost, type MatchHost } from './matchHost'
import { decodeClient, decodeServer, encode, type ClientMessage, type ServerMessage } from './protocol'
import type { MessageListener, Transport } from './transport'

/* --- 타입 ------------------------------------------------------------------------ */

export type LoopbackTransport = Transport &
  Readonly<{
    /** 안고 있는 호스트. 디버그 훅(`__weirdRoom.match`)과 테스트가 원본을 읽는 문이다 - 세션은 여기를 읽지 않는다. */
    host: MatchHost
    /** 이 사람의 좌석. 호스트가 `by`로 찍는 신원이다. */
    me: PlayerId
    /** 누적된 호스트 시각(ms). 프레임 델타의 합. */
    nowMs: () => number
  }>

/* --- 코덱 왕복 -------------------------------------------------------------------- */

/** 클라이언트 → 호스트 방향의 왕복. 세션이 만든 메시지가 코덱을 못 지나면 세션의 버그다 - 던진다. */
function roundTripClient(msg: ClientMessage): ClientMessage {
  const decoded = decodeClient(encode(msg))
  if (!decoded.ok) {
    throw new Error(`loopback: 클라이언트 메시지 '${msg.type}'가 코덱을 지나지 못했다 (${decoded.reason})`)
  }
  return decoded.message
}

/** 호스트 → 클라이언트 방향의 왕복. 호스트가 만든 메시지가 코덱을 못 지나면 직렬화 드리프트다 - 던진다(머리말). */
function roundTripServer(msg: ServerMessage): ServerMessage {
  const decoded = decodeServer(encode(msg))
  if (!decoded.ok) {
    throw new Error(`loopback: 서버 메시지 '${msg.type}'가 코덱을 지나지 못했다 (${decoded.reason}) - 직렬화 드리프트`)
  }
  return decoded.message
}

/* --- 전송 ------------------------------------------------------------------------ */

/**
 * 호스트 하나를 안은 전송. `me`는 이 전송이 대변하는 좌석이고, 호스트가 `me`에게 보내는 것만 구독자에게 배달된다 -
 * 봇에게 가는 메시지는 받을 클라이언트가 없다. 좌석은 여기서 앉히지 않는다(`createBotMatch`가 한다) - 전송은 앉은 뒤의 문이다.
 */
export function createLoopbackTransport(host: MatchHost, me: PlayerId): LoopbackTransport {
  let clockMs = 0
  let closed = false
  const listeners: MessageListener[] = []

  const unsubscribe = host.onSend((to, msg) => {
    if (closed || to !== me) return
    // 왕복은 콜스택 안에서 - 드리프트는 보낸 자리에서 터져야 스택이 원인을 가리킨다. 배달만 미룬다.
    const delivered = roundTripServer(msg)
    queueMicrotask(() => {
      if (closed) return
      for (const listener of [...listeners]) listener(delivered)
    })
  })

  return {
    host,
    me,
    nowMs: () => clockMs,

    send(msg: ClientMessage): void {
      if (closed) return
      host.receive(me, roundTripClient(msg), clockMs)
    },

    onMessage(listener: MessageListener): () => void {
      listeners.push(listener)
      return () => {
        const at = listeners.indexOf(listener)
        if (at >= 0) listeners.splice(at, 1)
      }
    },

    tick(dtMs: number): void {
      // 유한한 양수만 시계를 민다. 탭이 잠들었다 깨어난 첫 프레임의 거대한 dt는 그대로 흐른다 - 봇 판에서는 그것이 옳다(머리말).
      if (closed || !(dtMs > 0) || !Number.isFinite(dtMs)) return
      clockMs += dtMs
      host.tick(clockMs)
    },

    close(): void {
      if (closed) return
      closed = true
      unsubscribe()
      listeners.length = 0
    },
  }
}

/* --- 봇과 한 판 -------------------------------------------------------------------- */

export type BotSeatSpec = Readonly<{ id: PlayerId; name: string; difficulty: BotDifficulty }>

export type BotMatchOptions = Readonly<{
  seed: string
  deps: MatchDeps
  /** 사람 좌석. 첫 좌석이므로 방장이다. `magazines`는 계정의 것(`progress.ts`)이라 여기로 들어온다. */
  me: Readonly<{ id: PlayerId; name: string; magazines: number }>
  bots: readonly BotSeatSpec[]
  /** 루프백 방 코드. 서버가 발급하는 것이 아니라 표시용이다. */
  roomCode?: string
  config?: MatchConfig
}>

/** 루프백 방의 기본 코드. 서버 방 코드(대문자 네 자리, M6)와 겹치지 않게 길다. */
export const LOOPBACK_ROOM_CODE = 'LOCAL'

/**
 * 봇과 한 판. 호스트를 세우고 사람을 먼저(방장), 봇들을 그 뒤에 앉힌다 - `order`가 입장 순서이고 체인저 로테이션이라
 * 1라운드 체인저는 `order[1 % n]` = 첫 봇이다(사람은 첫 라운드에 술래다. 첫 판에서 바꾸기 화면을 보여 줄 수 없다 - M4).
 * 준비·시작은 세션이 `act`로 보낸다(`ready` → `start`). 여기서 대신 누르면 로비가 없는 흐름이 굳는다.
 */
export function createBotMatch(options: BotMatchOptions): LoopbackTransport {
  const host = createMatchHost({
    roomCode: options.roomCode ?? LOOPBACK_ROOM_CODE,
    seed: options.seed,
    deps: options.deps,
    ...(options.config === undefined ? {} : { config: options.config }),
  })
  const transport = createLoopbackTransport(host, options.me.id)
  host.join({ id: options.me.id, name: options.me.name, kind: 'human', bot: null }, options.me.magazines)
  for (const bot of options.bots) {
    host.join({ id: bot.id, name: bot.name, kind: 'cpu', bot: botProfileFor(bot.difficulty, options.deps.tuning) }, 0)
  }
  return transport
}
